/**
 * DashboardV2 — pixel-faithful rebuild (UI-v2 Phase 1b fidelity).
 *
 * Rebuilt to FAITHFULLY match design/v2-direction/index.html `#view-dashboard`
 * (lines 710–761) per DASHBOARD_FIDELITY_SPEC.md. The Phase 1a spine is
 * REPLACED. This screen must pass all HoD acceptance criteria before merge.
 *
 * Layout:
 *   1. Page head — "Overview" eyebrow + "Dashboard" title + open-roles sub +
 *      role selector + "Last 30 days" outline btn + "View candidates" primary
 *   2. KPI grid — 3-up (3→2→1 col responsive), 16px gap, 28px metrics,
 *      sparklines, footer sublabels, delta badges
 *   3. Two-column body — 1.42fr / 1fr, 16px gap:
 *      Left: Top candidates (rank badge + face + name/role + band-colored fit bar)
 *      Right: Pipeline by stage (horizontal opacity-graded blue bars — NOT funnel %)
 *
 * ── v2 Dashboard parity (v2-full-walkthrough-gaps.md §2) ────────────────────
 * D1 — Pipeline by stage always emits ALL 5 canonical stages
 *      (New/Screened/Interviewing/Offer/Hired) in order, count 0 on miss.
 *      Stages absent from the API are ZERO-FILLED, never dropped (the old
 *      code `.filter()`ed missing stages out — the slug-match drop bug).
 * D4 — JD/role picker restored. The dashboard scopes to `?jd=` (URL is the
 *      source of truth, matching V1 `Dashboard`), defaulting to the most
 *      recent JD. Top-candidates + pipeline both follow the selected role.
 * D6 — Rich empty / first-run / pre-rank states restored (V1 `FirstRunPanel`,
 *      "no jobs" first-run, "add candidates" coachmark, pre-rank pending,
 *      "select a job"). No more dead one-liners.
 *
 * ── Hard Constraints (auto-reject by founder if violated) ───────────────────
 * 1. "Interviews this week" label is LOCKED — do NOT relabel.
 * 2. Sparklines are HONEST — no invented data.
 *
 * ── Token discipline ─────────────────────────────────────────────────────────
 * No raw hex literals in this file. All colors via CSS var tokens or Tailwind
 * semantic utilities (text-text-*, border-border-*, bg-surface-*). Guard-clean.
 */
import { useEffect, useMemo } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTenantBase } from "@/hooks/useTenantBase";
import { Plus } from "lucide-react";
import { useIntelligenceSummary } from "@/hooks/queries/intelligence/useIntelligenceSummary";
import { useJds } from "@/hooks/queries/candidates/useJds";
import { useCandidates } from "@/hooks/queries/candidates/useCandidates";
import { useRankedJdCandidates } from "@/hooks/queries/candidates/useRankedJdCandidates";
import { useJdStageCounts } from "@/hooks/queries/candidates/useJdStageCounts";
import { useMe } from "@/hooks/queries/auth/useMe";
import { useDismissOnboarding } from "@/hooks/queries/auth/useDismissOnboarding";
import { FirstRunPanel } from "@/components/onboarding/FirstRunPanel";
import { SampleDataBanner } from "@/components/dashboard/SampleDataBanner";
import { Button } from "@/components/ui/button";
import { KpiCard } from "@/components/v2/KpiCard";
import { CandidateAvatar } from "@/components/v2/CandidateAvatar";
import { PipelineByStage } from "@/components/v2/PipelineByStage";
import { cn } from "@/lib/utils";

// ── Fit-band thresholds (spec §3.1: strong ≥85, moderate 65–84, weak <65) ──
function fitBand(score: number | null | undefined): "strong" | "moderate" | "weak" {
  if (score == null) return "weak";
  if (score >= 85) return "strong";
  if (score >= 65) return "moderate";
  return "weak";
}

// ── Canonical pipeline stages (spec §3.2) — ALWAYS all 5, in this order. ──
// D1: a stage absent from the API is zero-filled here, never dropped.
const CANONICAL_STAGE_LABELS = ["New", "Screened", "Interviewing", "Offer", "Hired"];

// Map each canonical funnel label to the tenant's default system stage slugs it
// aggregates. The stage-counts API returns the raw default stages (active /
// phone_screen / interview_scheduled / interviewed / offer_extended / hired);
// without this the label→slug guess only ever matched "hired", so the card
// rendered New/Screened/Interviewing/Offer as a permanent 0 regardless of the
// real pipeline. "Interviewing" folds interview_scheduled + interviewed into one
// bucket (mirrors the Hiring-Intelligence funnel's Interviewing definition).
const CANONICAL_STAGE_SLUGS: Record<string, readonly string[]> = {
  New: ["new", "active"],
  Screened: ["screened", "phone_screen"],
  Interviewing: ["interviewing", "interview_scheduled", "interviewed"],
  Offer: ["offer", "offer_extended"],
  Hired: ["hired"],
};

// ── v2 role selector — native <select>, token-styled to match the outline
//    button chrome. JD selection lives in the URL (?jd=), matching V1. ──
function RoleSelect({
  jds,
  value,
  onChange,
  disabled,
}: {
  jds: { id: string; name: string }[];
  value: string | undefined;
  onChange: (jdId: string) => void;
  disabled?: boolean;
}): JSX.Element {
  const isEmpty = jds.length === 0;
  const selected = jds.find((j) => j.id === value);
  // Default the visible option to "All roles" when nothing is chosen yet (the
  // brief pre-writeback window); it matches a real <option> so React stays
  // controlled and the summary is workspace-wide in that window anyway.
  const selectValue = value ?? (isEmpty ? "" : ALL_ROLES);
  return (
    <div className="relative w-full max-w-[240px]">
      <select
        value={selectValue}
        onChange={(e) => {
          if (e.target.value) onChange(e.target.value);
        }}
        disabled={disabled || isEmpty}
        title={value === ALL_ROLES ? "All roles" : selected?.name}
        aria-label="Select role"
        className={cn(
          "w-full appearance-none truncate rounded-md border border-border-default",
          "bg-surface-card py-2 pl-3 pr-9 text-sm font-medium text-text-primary",
          "transition-colors hover:border-border-strong",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-border-focus",
          "disabled:cursor-not-allowed disabled:opacity-60",
        )}
      >
        {isEmpty ? (
          <option value="">No roles yet</option>
        ) : (
          <>
            {/* All-roles option — workspace-wide KPI totals (no per-JD scope). */}
            <option value={ALL_ROLES}>All roles</option>
            {jds.map((j) => (
              <option key={j.id} value={j.id}>
                {j.name}
              </option>
            ))}
          </>
        )}
      </select>
      {/* Chevron */}
      <svg
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden
      >
        <path
          fillRule="evenodd"
          d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
          clipRule="evenodd"
        />
      </svg>
    </div>
  );
}

// ── Centered empty/pre-rank state block (v2 tokens). ──
function StateBlock({
  title,
  body,
  height = 140,
  action,
}: {
  title: string;
  body: string;
  height?: number;
  action?: JSX.Element;
}): JSX.Element {
  return (
    <div
      className="mx-auto flex w-full max-w-sm flex-col items-center justify-center px-4 text-center"
      style={{ minHeight: `${height}px` }}
    >
      <h4 className="text-[15px] font-semibold text-text-primary">
        {title}
      </h4>
      <p className="mt-1 text-[13px] text-text-secondary">
        {body}
      </p>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

// ── All-roles sentinel — the role picker's "all-option". When the URL carries
//    ?jd=all the dashboard shows workspace-wide KPI totals (no per-JD scope);
//    per-JD surfaces (Top candidates / Pipeline) fall back to their "select a
//    role" state since they are inherently single-role. Kept out of `selectedJdId`
//    so it never leaks into per-JD routes/queries. ──
const ALL_ROLES = "all";

export default function DashboardV2(): JSX.Element {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const base = useTenantBase();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Raw ?jd= value: a JD id, the ALL_ROLES sentinel, or absent (first load).
  const rawJd = searchParams.get("jd");
  const isAllRoles = rawJd === ALL_ROLES;
  // The real selected JD id — undefined for all-roles or first-load. Everything
  // per-JD (top candidates, pipeline, candidate links, KPI scope) keys off this.
  const selectedJdId = isAllRoles ? undefined : (rawJd ?? undefined);

  // ── Data hooks (reused from V1) ─────────────────────────────────────────
  // KPI summary is scoped to the selected role (active_candidates +
  // interviews_this_week); undefined jd → workspace-wide totals (all-roles).
  const summary = useIntelligenceSummary("30d", selectedJdId);
  // All JDs — powers the role picker, the draft/active split, and first-run
  // detection (matches V1 `Dashboard`, which selects across every JD).
  const jdsQuery = useJds();
  const meQ = useMe();
  const dismissOnboarding = useDismissOnboarding();
  const tenantName = meQ.data?.tenant_name ?? null;

  const summaryData = summary.data;
  const allJds = useMemo(() => jdsQuery.data ?? [], [jdsQuery.data]);
  const activeJds = useMemo(
    () => allJds.filter((j) => j.status === "active"),
    [allJds],
  );

  // ── Default-JD writeback (D4) — mirror V1: newest JD wins when ?jd= absent.
  //    An explicit ?jd=all (all-roles) is a deliberate choice — do NOT clobber
  //    it back to a JD (guard on the RAW param, not selectedJdId which is
  //    undefined for both absent AND all-roles).
  useEffect(() => {
    if (rawJd) return;
    if (!jdsQuery.isSuccess) return;
    if (allJds.length === 0) return;
    const sorted = [...allJds].sort(
      (a, b) => +new Date(b.created_at) - +new Date(a.created_at),
    );
    const first = sorted[0];
    if (!first) return;
    const next = new URLSearchParams(searchParams);
    next.set("jd", first.id);
    setSearchParams(next, { replace: true });
  }, [rawJd, jdsQuery.isSuccess, allJds, searchParams, setSearchParams]);

  // Role-picker change — writes the raw value (a JD id or the ALL_ROLES sentinel).
  function onJdChange(next: string): void {
    const params = new URLSearchParams(searchParams);
    params.set("jd", next);
    setSearchParams(params);
  }

  const selectedJd = useMemo(
    () => (selectedJdId ? allJds.find((j) => j.id === selectedJdId) ?? null : null),
    [selectedJdId, allJds],
  );

  // Top candidates: ranked candidates from the selected JD.
  const rankedQuery = useRankedJdCandidates({ jdId: selectedJdId });
  const ranked = rankedQuery.data ?? null; // null = pre-rank (or no JD).
  const topCandidates = useMemo(() => (ranked ?? []).slice(0, 5), [ranked]);

  // All candidates for the JD — distinguishes "no candidates" from "pre-rank"
  // (candidates uploaded, ranking still computing). Matches V1.
  const candidatesQuery = useCandidates({ jdId: selectedJdId });
  const candidatesForJd = useMemo(
    () => (selectedJdId ? candidatesQuery.data ?? [] : []),
    [selectedJdId, candidatesQuery.data],
  );
  const hasCandidates = candidatesForJd.length > 0;

  // D7: sample-data honesty affordance — seeded demo rows carry `is_sample`.
  const hasSampleData = candidatesForJd.some((c) => c.is_sample);

  // Pipeline by stage (D1): ALWAYS all 5 canonical stages, in order, 0 on miss.
  const stageQuery = useJdStageCounts({ jdId: selectedJdId });
  const pipelineStages = useMemo(() => {
    const counts = stageQuery.data ?? [];
    const bySlug = new Map(counts.map((s) => [s.slug, s]));
    const byName = new Map(counts.map((s) => [s.name.toLowerCase(), s]));
    return CANONICAL_STAGE_LABELS.map((label) => {
      // Sum every default stage slug this canonical label aggregates (e.g.
      // Interviewing = interview_scheduled + interviewed). Fall back to a
      // direct slug/name guess so custom tenant stage names still resolve.
      const slugs = CANONICAL_STAGE_SLUGS[label] ?? [label.toLowerCase().replace(/\s+/g, "_")];
      let count = 0;
      let matched = false;
      for (const slug of slugs) {
        const hit = bySlug.get(slug);
        if (hit) {
          count += hit.count;
          matched = true;
        }
      }
      if (!matched) {
        const byNameHit = byName.get(label.toLowerCase());
        if (byNameHit) count = byNameHit.count;
      }
      return { label, count };
    });
  }, [stageQuery.data]);

  // ── Sparkline data (honest only — no invented series) ─────────────────────
  const activePts: number[] = useMemo(() => {
    const curr = summaryData?.active_candidates;
    // D2: no prior available for active_candidates — an honest FLAT line. Return
    // a 2-point [v, v] series; a 1-element array made `smoothPath` emit no
    // stroke, so the sparkline rendered blank.
    if (curr == null) return [0, 0];
    return [curr, curr];
  }, [summaryData]);

  const openRolesPts: number[] = useMemo(() => {
    const v = summaryData?.active_jds ?? 0;
    return [v, v];
  }, [summaryData]);

  const interviewsThisWeek = summaryData?.interviews_this_week ?? null;
  const interviewsPts: number[] = useMemo(() => {
    const v = summaryData?.interviews_this_week ?? 0;
    return [v, v];
  }, [summaryData]);

  // ── KPI values ────────────────────────────────────────────────────────────
  // Active candidates + Interviews this week are role-scoped when a specific
  // role is selected (via the ?jd= summary param) and workspace-wide for
  // all-roles. Open roles (active_jds) stays workspace-wide regardless.
  const activeCandidates = summaryData?.active_candidates ?? null;
  const openRoles = summaryData?.active_jds ?? null;
  const draftJds = allJds.filter((j) => j.status === "draft").length;
  const activeOnlyJds = activeJds.length;

  const openRolesCount = openRoles ?? "—";

  // Honest KPI foots — name the scope so a role-scoped number never reads as a
  // workspace total (and vice-versa).
  const activeFoot = selectedJdId ? "In this role's pipeline" : "Across all roles";
  const interviewsFoot = selectedJdId
    ? "This role · active interview stage"
    : "Candidates in an active interview stage";

  // ── Navigation targets (v2 shell) ──────────────────────────────────────────
  const candidatesHref = `${base}/candidates${
    selectedJdId ? `?jd=${encodeURIComponent(selectedJdId)}` : ""
  }`;
  const addCandidateHref = `${base}/candidates/new${
    selectedJdId ? `?jd=${encodeURIComponent(selectedJdId)}` : ""
  }`;
  const createJobHref = `${base}/jobs/new`;

  // Top-candidate row → candidate detail, carrying the selected-role JD context
  // (same pattern as the candidates table's row click, CandidatesV2 onRowClick).
  const candidateHref = (candidateId: string): string =>
    `${base}/candidates/${candidateId}${
      selectedJdId ? `?jd=${encodeURIComponent(selectedJdId)}` : ""
    }`;

  function goToCandidates(): void {
    navigate(candidatesHref);
  }

  // ── Page head (shared across states) ───────────────────────────────────────
  const pageHead = (
    <div className="flex items-center gap-[14px]" style={{ marginBottom: "22px" }}>
      <div className="min-w-0 flex-1">
        <div
          className="text-[hsl(var(--brand,214_100%_41%))] font-bold uppercase"
          style={{ fontSize: "11px", letterSpacing: "0.08em" }}
        >
          Overview
        </div>
        <h1
          className="text-text-primary"
          style={{ fontSize: "22px", fontWeight: 650, letterSpacing: "-0.02em", margin: 0 }}
        >
          Dashboard
        </h1>
        <div
          className="text-text-secondary"
          style={{ fontSize: "13.5px", marginTop: "3px" }}
        >
          Hiring across {openRolesCount} open role{openRolesCount !== 1 ? "s" : ""}
        </div>
      </div>

      {/* The control cluster only makes sense once at least one role exists —
          suppressed on the first-run / no-jobs branch (title-only head, V1 parity). */}
      {allJds.length > 0 ? (
        <>
          {/* D4: role selector (now with an "All roles" all-option). Bind to the
              RAW ?jd= value so the sentinel round-trips. */}
          <RoleSelect
            jds={allJds.map((j) => ({ id: j.id, name: j.name }))}
            value={rawJd ?? undefined}
            onChange={onJdChange}
            disabled={!jdsQuery.isSuccess}
          />

          {/* D11: the summary window is fixed at 30 days — render it as an honest,
              non-interactive range label (a chip styled like the outline button),
              NOT an inert button that implies a range picker we don't ship. */}
          <span
            className="inline-flex shrink-0 items-center rounded-md border border-border-default bg-surface-card px-3 py-1.5 text-sm font-medium text-text-secondary"
            title="Metrics reflect the last 30 days"
          >
            Last 30 days
          </span>
          <Button variant="outline" size="sm" onClick={goToCandidates}>
            View candidates
          </Button>
          {/* D5: primary Add-candidates CTA (V1 parity — was missing from v2). */}
          <Button asChild size="sm">
            <Link to={addCandidateHref}>
              <Plus aria-hidden />
              Add candidates
            </Link>
          </Button>
        </>
      ) : null}
    </div>
  );

  // ── JD-list error branch (C5) ──────────────────────────────────────────────
  // The role picker, KPI foots, and per-JD surfaces all key off the JD list; if
  // it fails the page would otherwise render KPI em-dashes with a disabled role
  // picker and no explanation. Surface an honest error card + Retry instead
  // (mirrors the JdsV2 / JdDetailV2 house style: `text-verdict-gap-fg` copy,
  // server message when present). pageHead degrades to title-only here because
  // `allJds` is empty on error.
  if (jdsQuery.isError) {
    return (
      <div>
        {pageHead}
        <div
          className="rounded-[var(--radius)] border border-border-default bg-surface-card shadow-[var(--shadow-sm)]"
          style={{ padding: "40px 20px" }}
          data-testid="dashboard-jds-error"
        >
          <div className="flex flex-col items-center gap-3 text-center">
            <p className="text-[13.5px] text-verdict-gap-fg">
              {jdsQuery.error?.message ??
                "Couldn't load your roles. Refresh to try again."}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void jdsQuery.refetch()}
              data-testid="dashboard-jds-error-retry"
            >
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── First-run / no-jobs branch (D6) ────────────────────────────────────────
  // No JDs exist yet: show the first-run onboarding panel + the rich
  // "set up your first job" empty state instead of an empty dashboard.
  if (jdsQuery.isSuccess && allJds.length === 0) {
    return (
      <div>
        {pageHead}
        <FirstRunPanel
          tenantSlug={tenantSlug}
          /* PR4: was a hardcoded `/${tenantSlug}` — a "v1-shaped base" that, while
             v2 lived at /v2, pointed FirstRunPanel's CTAs into the v1 tree. Post-
             flip the tenant root IS v2, so the hook returns the same string and
             the CTAs finally land in v2. Routed through useTenantBase() so this
             site cannot drift out of sync the next time the base moves. */
          basePath={base}
          tenantName={tenantName}
        />
        <div
          className="rounded-[var(--radius)] border border-border-default bg-surface-card shadow-[var(--shadow-sm)]"
          style={{ padding: "40px 20px" }}
        >
          <StateBlock
            height={160}
            title="Let's set up your first job"
            body="Add a job description and CertAIn will help you triage candidates against it."
            action={
              <Button asChild>
                <Link to={createJobHref}>
                  <Plus aria-hidden />
                  Create your first job
                </Link>
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* D7: sample-data banner — rendered in-flow at the top of the dashboard
          content (the v2 shell has no above-chrome portal; SH3 is out of scope).
          Self-hides when no seeded rows are present. */}
      {hasSampleData ? (
        <div className="mb-4 overflow-hidden rounded-[var(--radius)] border border-border-default">
          <SampleDataBanner hasSampleData={hasSampleData} />
        </div>
      ) : null}

      {pageHead}

      {/* First-run onboarding (self-gates to nothing for demo/non-first-run). */}
      <FirstRunPanel
          tenantSlug={tenantSlug}
          /* PR4: was a hardcoded `/${tenantSlug}` — a "v1-shaped base" that, while
             v2 lived at /v2, pointed FirstRunPanel's CTAs into the v1 tree. Post-
             flip the tenant root IS v2, so the hook returns the same string and
             the CTAs finally land in v2. Routed through useTenantBase() so this
             site cannot drift out of sync the next time the base moves. */
          basePath={base}
          tenantName={tenantName}
        />

      {/* ── KPI GRID — 3-up / 2-up responsive at the canonical 1160px ─── */}
      <div
        className="grid gap-[16px] grid-cols-1 sm:grid-cols-2 min-[1160px]:grid-cols-3"
        style={{ marginBottom: "16px" }}
      >
        {/* D3/D12: no period-over-period series exists for active_candidates in
            the summary payload, so a "▲23% / +8 vs last week" delta would be
            fabricated. Suppress the delta (null) and use a descriptive, honest
            foot. (The old literal "▲23%" also double-drew the badge glyph.) */}
        <KpiCard
          label="Active candidates"
          metric={activeCandidates != null ? String(activeCandidates) : "—"}
          delta={null}
          foot={activeFoot}
          sparkPts={activePts}
          sparkCvar="--chart-2"
        />

        {/* D12: "±0" claims a real no-change WoW we can't source — suppress it.
            The foot IS real (active/draft split from the JD list). */}
        <KpiCard
          label="Open roles"
          metric={openRoles != null ? String(openRoles) : "—"}
          delta={null}
          foot={jdsQuery.data ? `${activeOnlyJds} open · ${draftJds} draft` : "—"}
          sparkPts={openRolesPts}
          sparkCvar="--chart-5"
        />

        {/* D9: label LOCKED. Now backed by a real point-in-time metric —
            candidates currently in an active interview stage (interview_scheduled
            or interviewed), from the summary payload. Em-dash only until the
            summary loads; the foot describes the honest source. */}
        <KpiCard
          label="Interviews this week"
          metric={interviewsThisWeek != null ? String(interviewsThisWeek) : "—"}
          delta={null}
          foot={interviewsFoot}
          sparkPts={interviewsPts}
          sparkCvar="--chart-2"
        />
      </div>

      {/* ── TWO-COLUMN BODY — 1.42fr 1fr, gap 16px, collapse at 1160px ── */}
      <div className="grid grid-cols-1 gap-[16px] min-[1160px]:grid-cols-[1.42fr_1fr]">

        {/* ── LEFT CARD: Top candidates ─────────────────────────────── */}
        <div className="rounded-[var(--radius)] border border-border-default bg-surface-card shadow-[var(--shadow-sm)]">
          <div
            className="flex items-start justify-between gap-[10px]"
            style={{ padding: "18px 20px 0" }}
          >
            <div>
              <h3
                className="text-text-primary"
                style={{ fontSize: "15px", fontWeight: 600, letterSpacing: "-0.01em", margin: 0 }}
              >
                Top candidates
              </h3>
              {/* D8: the list is scoped to the SELECTED role (via ?jd=), not a
                  cross-role merge — so the subtitle names the role, honestly
                  matching its source (was the misleading "across open roles"). */}
              <p
                className="text-text-secondary"
                style={{ fontSize: "13px", marginTop: "2px" }}
              >
                {selectedJd
                  ? `Ranked by AI fit · ${selectedJd.name}`
                  : "Ranked by AI fit for the selected role"}
              </p>
            </div>
            {ranked != null && ranked.length > 0 ? (
              <Button variant="ghost" size="sm" onClick={goToCandidates}>
                View all
              </Button>
            ) : null}
          </div>

          <div style={{ padding: "6px 20px 18px" }}>
            {!selectedJdId ? (
              // D6: no role selected.
              <StateBlock
                title="Select a job"
                body="Choose a role above to view AI-ranked candidates."
              />
            ) : rankedQuery.isLoading || candidatesQuery.isLoading ? (
              <div className="flex h-[140px] items-center justify-center text-sm text-text-tertiary">
                Loading…
              </div>
            ) : ranked != null && ranked.length > 0 ? (
              topCandidates.map((cand, i) => {
                const rank = i + 1;
                const score = cand.fit_score ?? null;
                const band = fitBand(score);
                return (
                  // CHANGE 1: the whole row is a real anchor to the candidate
                  // detail page, carrying the selected-role JD context (?jd=) so
                  // the candidate opens on that role's ranking — same target the
                  // candidates table row-click uses. Keyboard-focusable with a
                  // token-driven hover/focus state (no raw hex).
                  <Link
                    key={cand.candidate_id}
                    to={candidateHref(cand.candidate_id)}
                    aria-label={
                      selectedJd
                        ? `View ${cand.name} for ${selectedJd.name}`
                        : `View ${cand.name}`
                    }
                    className={cn(
                      "flex items-center gap-[11px] rounded-md",
                      "border-b border-border-default",
                      "hover:bg-surface-subtle/40 transition-colors",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-border-focus",
                    )}
                    style={{ padding: "9px 0" }}
                  >
                    <span
                      className="shrink-0 grid place-items-center tabular-nums"
                      style={{
                        width: "24px",
                        height: "24px",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: 650,
                        background: "hsl(var(--brand,214_100%_41%) / 0.12)",
                        color: "hsl(var(--brand,214_100%_41%))",
                        flexShrink: 0,
                      }}
                    >
                      {rank}
                    </span>

                    <CandidateAvatar name={cand.name} size="xs" ring={false} />

                    <div className="min-w-0 flex-1">
                      <div
                        className="truncate text-text-primary"
                        style={{ fontWeight: 600 }}
                      >
                        {cand.name}
                      </div>
                      <div
                        className="truncate text-text-secondary"
                        style={{ fontSize: "12px" }}
                      >
                        {selectedJd?.name ?? "—"}
                      </div>
                    </div>

                    <div
                      className="flex items-center gap-[10px] shrink-0"
                      style={{ width: "108px" }}
                    >
                      <div
                        className="flex-1 overflow-hidden rounded-full"
                        style={{ height: "6px", background: "var(--surface-subtle)", minWidth: "60px" }}
                      >
                        <span
                          className="block h-full rounded-full"
                          style={{
                            width: `${score ?? 0}%`,
                            background:
                              band === "strong"
                                ? "hsl(var(--strong,165_88%_30%))"
                                : band === "moderate"
                                  ? "hsl(var(--moderate,36_88%_40%))"
                                  : "hsl(var(--weak,0_72%_46%))",
                          }}
                        />
                      </div>
                      <span
                        className="tabular-nums text-text-primary text-right"
                        style={{ fontSize: "13.5px", fontWeight: 650, width: "24px" }}
                      >
                        {score ?? "—"}
                      </span>
                    </div>
                  </Link>
                );
              })
            ) : hasCandidates ? (
              // D6: pre-rank — candidates uploaded, ranking still computing.
              <div
                data-testid="ranking-pending-section"
                className="flex h-[140px] flex-col items-center justify-center gap-3 text-center"
              >
                <span
                  className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-text-tertiary border-t-transparent"
                  role="status"
                  aria-hidden
                />
                <div>
                  <h4 className="text-[15px] font-semibold text-text-primary">
                    Ranking {candidatesForJd.length}{" "}
                    {candidatesForJd.length === 1 ? "candidate" : "candidates"}…
                  </h4>
                  <p className="mt-1 text-[13px] text-text-secondary">
                    CertAIn is scoring uploaded candidates against this role.
                  </p>
                </div>
              </div>
            ) : (
              // D6: no candidates yet — coachmark with add-candidates CTA.
              <StateBlock
                title="Add candidates to start ranking"
                body="Upload candidate resumes and CertAIn will rank them against this job."
                action={
                  <Button asChild onClick={() => dismissOnboarding.mutate()}>
                    <Link to={addCandidateHref}>
                      <Plus aria-hidden />
                      Add candidates
                    </Link>
                  </Button>
                }
              />
            )}
          </div>
        </div>

        {/* ── RIGHT CARD: Pipeline by stage ─────────────────────────── */}
        <div className="rounded-[var(--radius)] border border-border-default bg-surface-card shadow-[var(--shadow-sm)]">
          <div style={{ padding: "18px 20px 0" }}>
            <h3
              className="text-text-primary"
              style={{ fontSize: "15px", fontWeight: 600, letterSpacing: "-0.01em", margin: 0 }}
            >
              Pipeline by stage
            </h3>
            <p
              className="text-text-secondary"
              style={{ fontSize: "13px", marginTop: "2px" }}
            >
              {selectedJd?.name ?? "—"}
            </p>
          </div>

          <div style={{ padding: "18px 20px" }}>
            {!selectedJdId ? (
              <StateBlock
                height={120}
                title="Select a job"
                body="Choose a role above to see its pipeline."
              />
            ) : stageQuery.isLoading ? (
              <div className="flex h-[120px] items-center justify-center text-sm text-text-tertiary">
                Loading…
              </div>
            ) : (
              // D1: ALWAYS all 5 canonical stages, zero-filled on miss.
              // (roleTitle is rendered by the parent card head, not the chart —
              // dead prop dropped, D22.)
              <PipelineByStage stages={pipelineStages} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
